/**
 * Individual image card with drag handle, description popover, and action menu
 */

import { useState } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { ListingImage, DescriptionStatus } from '../types';

interface ImageCardProps {
  image: ListingImage;
  isAutoHero: boolean;
  onDelete: () => void;
  onCopyUrl: () => void;
  onImageClick: () => void;
  onSetHero: () => void;
}

/**
 * Get status indicator styles based on description status
 */
function getStatusStyles(status: DescriptionStatus): {
  show: boolean;
  backgroundColor: string;
  text: string;
  tooltip: string;
} {
  switch (status) {
    case 'complete':
      return { show: false, backgroundColor: '', text: '', tooltip: '' };
    case 'generating':
      return {
        show: true,
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        text: 'Generating...',
        tooltip: 'Description is being generated',
      };
    case 'delayed':
      return {
        show: true,
        backgroundColor: 'rgba(234, 179, 8, 0.9)',
        text: 'Retrying',
        tooltip: 'Generation delayed - retrying automatically',
      };
    case 'failed':
      return {
        show: true,
        backgroundColor: 'rgba(220, 38, 38, 0.9)',
        text: 'Retrying',
        tooltip: 'Multiple failures - still retrying automatically',
      };
  }
}

export function ImageCard({
  image,
  isAutoHero,
  onDelete,
  onCopyUrl,
  onImageClick,
  onSetHero,
}: ImageCardProps) {
  const [showMenu, setShowMenu] = useState(false);
  const [showDescriptionPopover, setShowDescriptionPopover] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [imageError, setImageError] = useState(false);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: image.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const handleCopyUrl = () => {
    onCopyUrl();
    setShowMenu(false);
  };

  const handleDelete = () => {
    setShowMenu(false);
    onDelete();
  };

  const statusStyles = getStatusStyles(image.description_status);
  const isGenerating = image.description_status === 'generating';
  const hasIssue = image.description_status === 'delayed' || image.description_status === 'failed';

  return (
    <div
      ref={setNodeRef}
      style={{
        ...style,
        position: 'relative',
        backgroundColor: 'white',
        borderRadius: '12px',
        overflow: 'hidden',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        border: image.is_hero === 1 ? '2px solid #c9a84c' : '1px solid #e5e7eb',
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => {
        setIsHovered(false);
        setShowMenu(false);
        setShowDescriptionPopover(false);
      }}
    >
      {/* Image */}
      <div
        style={{
          position: 'relative',
          aspectRatio: '3/2',
          backgroundColor: '#f3f4f6',
          cursor: 'pointer',
        }}
        onClick={onImageClick}
      >
        {imageError ? (
          <div
            style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#fef2f2',
              color: '#dc2626',
              fontSize: '12px',
              textAlign: 'center',
              padding: '8px',
            }}
          >
            <span style={{ fontSize: '24px', marginBottom: '4px' }}>⚠️</span>
            <span>Failed to load</span>
            <span style={{ fontSize: '10px', color: '#6b7280', marginTop: '2px' }}>
              {image.filename}
            </span>
          </div>
        ) : (
          <img
            src={image.thumbnail_url}
            alt={image.filename}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
            onError={() => setImageError(true)}
          />
        )}
        
        {/* Hero indicator — clickable star */}
        {(image.is_hero === 1 || isAutoHero || isHovered) && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onSetHero();
            }}
            style={{
              position: 'absolute',
              bottom: '8px',
              left: '8px',
              backgroundColor: image.is_hero === 1 ? '#c9a84c' : isAutoHero ? '#3b82f6' : 'rgba(0,0,0,0.6)',
              color: 'white',
              borderRadius: '6px',
              height: '28px',
              padding: '0 10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '4px',
              fontSize: '11px',
              fontWeight: 600,
              border: 'none',
              cursor: 'pointer',
              transition: 'background-color 0.2s',
              zIndex: 5,
            }}
            title={image.is_hero === 1 ? 'Remove as hero (revert to auto)' : isAutoHero ? 'Auto-selected hero (click to pin)' : 'Set as hero image'}
          >
            ★ {image.is_hero === 1 ? 'Hero' : isAutoHero ? 'Auto Hero' : 'Set Hero'}
          </button>
        )}

        {/* Description status indicator */}
        {statusStyles.show && (
          <div
            style={{
              position: 'absolute',
              bottom: '8px',
              left: '8px',
              backgroundColor: statusStyles.backgroundColor,
              color: 'white',
              borderRadius: '4px',
              padding: '2px 6px',
              fontSize: '11px',
              fontStyle: isGenerating ? 'italic' : 'normal',
            }}
            title={statusStyles.tooltip}
          >
            {statusStyles.text}
          </div>
        )}

        {/* Description icon (paragraph ☰) - top-left, after primary badge */}
        {isHovered && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowDescriptionPopover(!showDescriptionPopover);
            }}
            style={{
              position: 'absolute',
              top: '8px',
              left: '8px',
              backgroundColor: hasIssue
                ? 'rgba(107, 114, 128, 0.5)'
                : 'rgba(255, 255, 255, 0.9)',
              border: 'none',
              borderRadius: '6px',
              width: '28px',
              height: '28px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '14px',
              boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
              color: hasIssue ? '#9ca3af' : '#374151',
              opacity: hasIssue ? 0.7 : 1,
            }}
            title="View description"
          >
            {/* Paragraph icon with error overlay for failed states */}
            <span style={{ position: 'relative' }}>
              ☰
              {hasIssue && (
                <span
                  style={{
                    position: 'absolute',
                    top: '-4px',
                    right: '-6px',
                    fontSize: '10px',
                    color: image.description_status === 'failed' ? '#dc2626' : '#eab308',
                  }}
                >
                  ●
                </span>
              )}
            </span>
          </button>
        )}

        {/* Description popover */}
        {showDescriptionPopover && (
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'absolute',
              top: '40px',
              left: '8px',
              backgroundColor: 'white',
              borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              padding: '12px',
              zIndex: 20,
              minWidth: '200px',
              maxWidth: '280px',
            }}
          >
            {/* Close button */}
            <button
              onClick={() => setShowDescriptionPopover(false)}
              style={{
                position: 'absolute',
                top: '4px',
                right: '4px',
                backgroundColor: 'transparent',
                border: 'none',
                cursor: 'pointer',
                fontSize: '16px',
                color: '#9ca3af',
                padding: '4px',
                lineHeight: 1,
              }}
            >
              ×
            </button>

            {/* Popover content */}
            {image.description_status === 'complete' && image.description ? (
              <div style={{ fontSize: '12px', color: '#374151', whiteSpace: 'pre-wrap' }}>
                {image.description}
              </div>
            ) : image.description_status === 'generating' ? (
              <div style={{ fontSize: '12px', color: '#6b7280', fontStyle: 'italic' }}>
                ⏳ Generating description...
              </div>
            ) : image.description_status === 'delayed' ? (
              <div style={{ fontSize: '12px', color: '#92400e' }}>
                ⚠️ Generation delayed
                <div style={{ marginTop: '4px', fontSize: '11px', color: '#6b7280' }}>
                  Retrying automatically
                </div>
              </div>
            ) : (
              <div style={{ fontSize: '12px', color: '#991b1b' }}>
                ❌ Generation failed
                <div style={{ marginTop: '4px', fontSize: '11px', color: '#6b7280' }}>
                  Still retrying automatically
                </div>
              </div>
            )}
          </div>
        )}

        {/* Drag handle (visible on hover, bottom-right) */}
        {isHovered && !statusStyles.show && (
          <div
            {...attributes}
            {...listeners}
            style={{
              position: 'absolute',
              bottom: '8px',
              right: '8px',
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              border: 'none',
              borderRadius: '6px',
              width: '28px',
              height: '28px',
              cursor: 'grab',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '16px',
              boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
              color: '#6b7280',
            }}
            onClick={(e) => e.stopPropagation()}
            title="Drag to reorder"
          >
            ⋮⋮
          </div>
        )}

        {/* Menu button (visible on hover, top-right) */}
        {isHovered && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowMenu(!showMenu);
            }}
            style={{
              position: 'absolute',
              top: '8px',
              right: '8px',
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              border: 'none',
              borderRadius: '6px',
              width: '28px',
              height: '28px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '16px',
              boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
            }}
          >
            ⋯
          </button>
        )}

        {/* Dropdown menu */}
        {showMenu && (
          <div
            style={{
              position: 'absolute',
              top: '40px',
              right: '8px',
              backgroundColor: 'white',
              borderRadius: '8px',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
              overflow: 'hidden',
              zIndex: 10,
              minWidth: '120px',
            }}
          >
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleCopyUrl();
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                width: '100%',
                padding: '10px 12px',
                border: 'none',
                backgroundColor: 'transparent',
                cursor: 'pointer',
                fontSize: '13px',
                textAlign: 'left',
              }}
            >
              Copy URL
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleDelete();
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                width: '100%',
                padding: '10px 12px',
                border: 'none',
                backgroundColor: 'transparent',
                cursor: 'pointer',
                fontSize: '13px',
                textAlign: 'left',
                color: '#dc2626',
              }}
            >
              Delete
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
